import { useEffect, useRef } from 'react';
import type React from 'react';
import { cn } from '../../utils/cn.ts';
import type { ComboboxOption, ComboboxRenderState } from './Combobox.types.ts';
import { Icon } from './Icon.tsx';

const NativeButton = 'button';

interface ComboboxOptionRowProps<TValue extends string> {
    id: string;
    option: ComboboxOption<TValue>;
    index: number;
    active: boolean;
    selected: boolean;
    query: string;
    optionClassName?: string;
    skipInitialActiveScroll?: boolean;
    renderOption?: (
        option: ComboboxOption<TValue>,
        state: ComboboxRenderState
    ) => React.ReactNode;
    onActiveIndexChange: (index: number) => void;
    onSelect: (option: ComboboxOption<TValue>) => void;
}

export function ComboboxOptionRow<TValue extends string>({
    id,
    option,
    index,
    active,
    selected,
    query,
    optionClassName,
    skipInitialActiveScroll = false,
    renderOption,
    onActiveIndexChange,
    onSelect,
}: ComboboxOptionRowProps<TValue>) {
    const optionRef = useRef<HTMLButtonElement>(null);
    const wasActiveRef = useRef(active);

    useEffect(() => {
        const wasActive = wasActiveRef.current;
        wasActiveRef.current = active;

        if (active) {
            if (skipInitialActiveScroll && wasActive) {
                return;
            }
            optionRef.current?.scrollIntoView?.({ block: 'nearest' });
        }
    }, [active, skipInitialActiveScroll]);

    return (
        <div className="m-0 p-0">
            <NativeButton
                ref={optionRef}
                id={id}
                type="button"
                role="option"
                aria-label={option.ariaLabel ?? option.label}
                aria-selected={selected}
                disabled={option.disabled}
                data-active={active ? 'true' : undefined}
                onFocus={() => onActiveIndexChange(index)}
                onMouseEnter={() => onActiveIndexChange(index)}
                onClick={() => onSelect(option)}
                className={cn(
                    'grid min-h-9 w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-md border-0 bg-transparent px-3 py-1.5 text-left text-gray-700 text-sm transition-colors dark:text-gray-200',
                    'hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 dark:focus-visible:bg-white/10 dark:hover:bg-white/10',
                    selected && 'bg-primary/10 font-medium text-gray-900 dark:bg-primary/20 dark:text-white',
                    active && !selected && 'bg-gray-100 dark:bg-white/10',
                    option.disabled && 'cursor-not-allowed opacity-50',
                    optionClassName
                )}
                style={option.indent ? { paddingLeft: `${12 + option.indent * 14}px` } : undefined}
            >
                {renderOption
                    ? renderOption(option, { active, selected, query })
                    : renderDefaultOptionContent(option, selected)}
            </NativeButton>
        </div>
    );
}

function renderDefaultOptionContent<TValue extends string>(
    option: ComboboxOption<TValue>,
    selected: boolean
) {
    return (
        <>
            {option.icon ? (
                <Icon
                    name={option.icon}
                    size="sm"
                    className={cn(selected ? 'text-primary' : 'text-gray-400')}
                />
            ) : <span />}
            <span className="min-w-0">
                <span className="block truncate">{option.label}</span>
                {option.description ? (
                    <span className="block truncate text-gray-500 text-xs dark:text-gray-400">
                        {option.description}
                    </span>
                ) : null}
            </span>
            {option.meta ? (
                <span className="whitespace-nowrap text-gray-500 text-xs dark:text-gray-400">
                    {option.meta}
                </span>
            ) : null}
        </>
    );
}
